import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Typography, Paper, CircularProgress, Breadcrumbs, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TableSortLabel, InputBase, Select, MenuItem, Snackbar,
  Collapse, IconButton, Button,
} from "@material-ui/core";
import { Alert, Autocomplete } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import "./rsuite-theme.css";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import { getActionLogs } from "app/api/action-log.api";
import { getAssignedCompanies } from "app/api/credit-transfer.api";
import { fetchTransferHistory } from "../redux/transfer-history.action";

const csvEscape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

// The merged search: pick a field, then free-text search it. Company is a
// separate dropdown, so only the text-based fields live here.
const SEARCH_FIELDS = [
  { value: "recipient", label: "Recipient" },
  { value: "pi_no", label: "PI / PO" },
];

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseLocalDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
// Default the date filter to the current month so far (1st → today).
const currentMonthRange = () => {
  const now = new Date();
  return {
    start: fmtDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    end: fmtDate(now),
  };
};
// Disable future dates in the range picker (today stays selectable).
const disableFutureDate = (date) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(date); d.setHours(0, 0, 0, 0);
  return d.getTime() > today.getTime();
};

const useStyles = makeStyles(() => ({
  breadcrumb: {
    marginBottom: 8,
    "& .MuiBreadcrumbs-separator": { color: "#98A2B3" },
  },
  crumbMuted: { fontSize: 13, color: "#667085" },
  crumbCurrent: { fontSize: 13, fontWeight: 600, color: "#344054" },
  pageHeadRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 20,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#101828",
    lineHeight: "30px",
  },
  exportBtn: {
    fontSize: 13,
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 8,
    padding: "7px 16px",
    border: "1px solid #D0D5DD",
    color: "#344054",
    backgroundColor: "#fff",
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "#F9FAFB", borderColor: "#98A2B3" },
  },

  // Filter bar — its own white card, distinct from the table below.
  filterBar: {
    backgroundColor: "#fff",
    border: "1px solid #E4E7EC",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
    padding: "16px 20px",
    marginBottom: 16,
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 14,
  },
  companyField: {
    width: 220,
    "& .MuiOutlinedInput-root": { borderRadius: 8 },
    "& .MuiOutlinedInput-input": { fontSize: 13.5 },
  },
  // Merged field-picker + free-text search, styled as one bordered group.
  searchGroup: {
    display: "flex",
    alignItems: "center",
    height: 40,
    minWidth: 300,
    flex: "1 1 300px",
    maxWidth: 420,
    border: "1px solid rgba(0,0,0,0.23)",
    borderRadius: 8,
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": { borderColor: "rgba(0,0,0,0.6)" },
    "&:focus-within": { borderColor: "#3A4D54", boxShadow: "0 0 0 1px #3A4D54" },
  },
  fieldSelect: {
    fontSize: 13.5,
    color: "#344054",
    minWidth: 116,
    flex: "none",
    "& .MuiSelect-select": { padding: "0 26px 0 12px" },
    "& .MuiSelect-select:focus": { backgroundColor: "transparent" },
  },
  searchDivider: {
    width: 1,
    height: 22,
    backgroundColor: "#E4E7EC",
    flex: "none",
  },
  searchInput: {
    flex: 1,
    fontSize: 13.5,
    color: "#344054",
    padding: "0 12px",
    "& .MuiInputBase-input::placeholder": { color: "#98A2B3", opacity: 1 },
  },
  searchBtn: {
    fontSize: 13.5,
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 8,
    height: 40,
    padding: "0 24px",
    flex: "none",
    backgroundColor: "#3A4D54",
    color: "#fff",
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "#2d3b41" },
    "&:disabled": { backgroundColor: "#D0D5DD", color: "#98A2B3" },
  },

  // Table section
  card: {
    border: "1px solid #E4E7EC",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
    overflow: "hidden",
  },
  tableHead: {
    "& .MuiTableCell-head": {
      fontSize: 11.5,
      fontWeight: 600,
      letterSpacing: 0.5,
      textTransform: "uppercase",
      color: "#667085",
      backgroundColor: "#FCFCFD",
      borderBottom: "1px solid #E4E7EC",
      padding: "10px 14px",
    },
  },
  expandCell: { width: 40 },
  tableRow: {
    "& .MuiTableCell-body": {
      fontSize: 14,
      color: "#344054",
      padding: "12px 14px",
      borderBottom: "1px solid #F2F4F7",
    },
    "&:last-child .MuiTableCell-body": { borderBottom: "none" },
    "&:hover": { backgroundColor: "#F9FAFB" },
  },
  dateText: {
    fontSize: 14,
    fontWeight: 500,
    color: "#101828",
    lineHeight: "20px",
    whiteSpace: "nowrap",
  },
  timeText: {
    fontSize: 12.5,
    color: "#667085",
    lineHeight: "18px",
    marginTop: 1,
  },
  userName: {
    fontSize: 14,
    fontWeight: 500,
    color: "#101828",
    lineHeight: "20px",
  },
  userEmail: {
    fontSize: 12.5,
    color: "#667085",
    lineHeight: "18px",
    marginTop: 1,
  },
  creditValue: {
    fontSize: 14,
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  creditPlus: { color: "#067647" },
  creditMinus: { color: "#B42318" },
  detailsBox: {
    padding: "14px 24px 18px 64px",
    backgroundColor: "#FBFCFC",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    color: "#98A2B3",
    marginBottom: 2,
  },
  detailValue: {
    fontSize: 13.5,
    color: "#344054",
    wordBreak: "break-word",
  },
  emptyState: {
    padding: "40px 24px",
    textAlign: "center",
    color: "#98A2B3",
    fontSize: 14,
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: "40px 24px",
  },
  pagination: {
    borderTop: "1px solid #E4E7EC",
    "& .MuiTablePagination-caption, & .MuiTablePagination-select": { fontSize: 13 },
  },
}));

export default function TransferHistoryPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { logs, total, isLoading, error } = useSelector((state) => state.transferHistory);

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [searchField, setSearchField] = useState("recipient");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(() => currentMonthRange().start);
  const [endDate, setEndDate] = useState(() => currentMonthRange().end);
  // Only these filters actually reach the API. The draft inputs (above) are
  // copied here — and the request fires — only when the user clicks Search, so
  // typing in the box no longer spams the endpoint.
  const [appliedFilters, setAppliedFilters] = useState(() => {
    const { start, end } = currentMonthRange();
    return { companyId: "", searchField: "recipient", search: "", startDate: start, endDate: end };
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState("created_at");
  const [orderDir, setOrderDir] = useState("desc");
  const [expandedId, setExpandedId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    getAssignedCompanies()
      .then((res) => setCompanies(res.data.data || []))
      .catch(() => setCompanies([]));
  }, []);

  // Copy the draft filter inputs into the applied set and jump back to page 1.
  // This is the ONLY path that triggers a filtered fetch — editing inputs does
  // not.
  const applyFilters = () => {
    setPage(0);
    setAppliedFilters({
      companyId,
      searchField,
      search: search.trim(),
      startDate,
      endDate,
    });
  };

  const buildFilterParams = () => {
    const params = {};
    if (appliedFilters.companyId) params.company_id = appliedFilters.companyId;
    if (appliedFilters.search) params[appliedFilters.searchField] = appliedFilters.search;
    if (appliedFilters.startDate) params.start_date = appliedFilters.startDate;
    if (appliedFilters.endDate) params.end_date = appliedFilters.endDate;
    return params;
  };

  useEffect(() => {
    const params = {
      page: page + 1, length: rowsPerPage, order_by: orderBy, order_dir: orderDir,
      ...buildFilterParams(),
    };
    setExpandedId(null);
    dispatch(fetchTransferHistory(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, page, rowsPerPage, appliedFilters, orderBy, orderDir]);

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDir((dir) => (dir === "desc" ? "asc" : "desc"));
    } else {
      setOrderBy(column);
      setOrderDir("desc");
    }
    setPage(0);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = {
        page: 1,
        length: 5000,
        action: "credit_transfer",
        order_by: orderBy,
        order_dir: orderDir,
        ...buildFilterParams(),
      };

      const response = await getActionLogs(params);
      const rows = response.data.data?.data || [];
      const header = [
        "Date", "Company", "Performed By", "Performed By Email",
        "Recipient", "Recipient Email", "Credits", "Module", "PI/PO", "Remark",
      ];
      const lines = [header.map(csvEscape).join(",")];
      rows.forEach((log) => {
        const isDebit = log.details?.transfer_action === "debit";
        const amount = log.amount !== null && log.amount !== undefined
          ? `${isDebit ? "-" : ""}${parseFloat(log.amount)}`
          : "";
        lines.push([
          new Date(log.created_at).toLocaleString(),
          log.company_name || (log.company_id ? `Company #${log.company_id}` : ""),
          log.actor_name || "",
          log.actor_email || "",
          log.target_user_name || "",
          log.target_user_email || "",
          amount,
          log.details?.module || "",
          log.details?.pi_no || "",
          log.details?.remark || "",
        ].map(csvEscape).join(","));
      });
      // BOM so Excel opens it as UTF-8
      const bom = String.fromCharCode(0xfeff);
      const blob = new Blob([bom + lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `transfer-history-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* fetch errors already surface via the redux snackbar */ }
    setIsExporting(false);
  };

  const hasFilters = !!(appliedFilters.companyId || appliedFilters.search
    || appliedFilters.startDate || appliedFilters.endDate);
  const searchPlaceholder = `Search ${(SEARCH_FIELDS.find((f) => f.value === searchField) || {}).label || ""}`;

  return (
    <Box>
      <Breadcrumbs
        className={classes.breadcrumb}
        separator={<NavigateNextIcon style={{ fontSize: 16 }} />}
        aria-label="breadcrumb"
      >
        <Typography className={classes.crumbMuted}>Credit Transfer</Typography>
        <Typography className={classes.crumbCurrent}>Transfer History</Typography>
      </Breadcrumbs>

      <Box className={classes.pageHeadRow}>
        <Typography className={classes.pageTitle}>Transfer History</Typography>
        <Button
          className={classes.exportBtn}
          onClick={handleExport}
          disabled={isExporting || total === 0}
          startIcon={isExporting
            ? <CircularProgress size={14} style={{ color: "#98A2B3" }} />
            : <GetAppIcon style={{ fontSize: 16 }} />}
        >
          Export CSV
        </Button>
      </Box>

      {/* Filters — separate white card, laid out left to right */}
      <Box className={classes.filterBar}>
        <Box className={classes.filterRow}>
          <Autocomplete
            options={companies}
            getOptionLabel={(c) => c.company_name || `Company #${c.company_id}`}
            getOptionSelected={(option, value) => option.company_id === value.company_id}
            value={companies.find((c) => c.company_id === companyId) || null}
            onChange={(e, value) => setCompanyId(value ? value.company_id : "")}
            size="small"
            className={classes.companyField}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder="All companies" />
            )}
          />
          <Box className={classes.searchGroup}>
            <Select
              disableUnderline
              className={classes.fieldSelect}
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
            >
              {SEARCH_FIELDS.map((f) => (
                <MenuItem key={f.value} value={f.value} style={{ fontSize: 13.5 }}>{f.label}</MenuItem>
              ))}
            </Select>
            <Box className={classes.searchDivider} />
            <InputBase
              className={classes.searchInput}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
              startAdornment={<SearchIcon style={{ fontSize: 18, color: "#98A2B3", marginRight: 6 }} />}
            />
          </Box>
          <DateRangePicker
            value={startDate && endDate ? [parseLocalDate(startDate), parseLocalDate(endDate)] : null}
            onChange={(range) => {
              if (range && range.length === 2) {
                setStartDate(fmtDate(range[0]));
                setEndDate(fmtDate(range[1]));
              } else {
                setStartDate("");
                setEndDate("");
              }
            }}
            format="dd MMM yyyy"
            character=" - "
            placement="bottomEnd"
            editable={false}
            ranges={[]}
            disabledDate={disableFutureDate}
            placeholder="Filter by date"
            style={{ width: 240 }}
          />
          <Button
            className={classes.searchBtn}
            onClick={applyFilters}
            disabled={isLoading}
            disableElevation
          >
            Search
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Paper className={classes.card} elevation={0}>
        {isLoading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress size={24} />
          </Box>
        ) : logs.length === 0 ? (
          <Typography className={classes.emptyState}>
            {hasFilters ? "No transfers match your filters" : "No transfers recorded yet"}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.expandCell} style={{ paddingLeft: 16 }} />
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "created_at"}
                      direction={orderBy === "created_at" ? orderDir : "desc"}
                      onClick={() => handleSort("created_at")}
                    >
                      Date &amp; Time
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Performed By</TableCell>
                  <TableCell>Recipient</TableCell>
                  <TableCell align="right" style={{ paddingRight: 24 }}>
                    <TableSortLabel
                      active={orderBy === "amount"}
                      direction={orderBy === "amount" ? orderDir : "desc"}
                      onClick={() => handleSort("amount")}
                    >
                      Credits
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => {
                  const created = new Date(log.created_at);
                  const isDebit = log.details?.transfer_action === "debit";
                  const amount = log.amount !== null && log.amount !== undefined
                    ? parseFloat(log.amount)
                    : null;
                  const actorName = log.actor_name || log.actor_email || "—";
                  const targetName = log.target_user_name || log.target_user_email || "—";
                  const rowId = log.uuid || log.id;
                  const isExpanded = expandedId === rowId;
                  return (
                    <React.Fragment key={rowId}>
                      <TableRow
                        className={classes.tableRow}
                        onClick={() => setExpandedId(isExpanded ? null : rowId)}
                        style={{ cursor: "pointer" }}
                      >
                        <TableCell className={classes.expandCell} style={{ paddingLeft: 16 }}>
                          <IconButton size="small" aria-label={isExpanded ? "Collapse details" : "Expand details"}>
                            {isExpanded
                              ? <KeyboardArrowUpIcon fontSize="small" />
                              : <KeyboardArrowDownIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography className={classes.dateText}>
                            {created.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                          </Typography>
                          <Typography className={classes.timeText}>
                            {created.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {log.company_name || (log.company_id ? `Company #${log.company_id}` : "—")}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography className={classes.userName}>{actorName}</Typography>
                            <Typography className={classes.userEmail}>{log.actor_email}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography className={classes.userName}>{targetName}</Typography>
                            <Typography className={classes.userEmail}>{log.target_user_email}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" style={{ paddingRight: 24 }}>
                          {amount !== null ? (
                            <span className={`${classes.creditValue} ${isDebit ? classes.creditMinus : classes.creditPlus}`}>
                              {isDebit ? "− " : "+ "}{amount.toLocaleString()}
                            </span>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={6} style={{ padding: 0, border: "none" }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box className={classes.detailsBox}>
                              <Box>
                                <Typography className={classes.detailLabel}>PI / PO</Typography>
                                <Typography className={classes.detailValue}>{log.details?.pi_no || "—"}</Typography>
                              </Box>
                              <Box>
                                <Typography className={classes.detailLabel}>Remark</Typography>
                                <Typography className={classes.detailValue}>{log.details?.remark || "—"}</Typography>
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          component="div"
          className={classes.pagination}
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onChangePage={(e, newPage) => setPage(newPage)}
          onChangeRowsPerPage={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {error && (
        <Snackbar open={!!error} autoHideDuration={5000}>
          <Alert severity="error">{error}</Alert>
        </Snackbar>
      )}
    </Box>
  );
}
